import { _Componentes_WebApiCalls_Post } from "../ComponentesGlobales/WebApiCalls";

export const ALICUOTAS_IVA = Object.freeze([0, 2.5, 5, 10.5, 21, 27]);

export const esAlicuotaIvaAdmitida = (valor) =>
  ALICUOTAS_IVA.includes(Number(valor));

export const formatearAlicuotaIva = (valor) =>
  `${new Intl.NumberFormat("es-AR", { maximumFractionDigits: 1 }).format(Number(valor))} %`;

export const etiquetaEstadoIva = (iva) =>
  iva?.IvaPendiente
    ? "IVA pendiente de configuración"
    : iva?.Configurado
    ? `IVA ${formatearAlicuotaIva(iva.PorcentajeIVA)} configurado`
    : "IVA sin configurar";

export const requiereConfirmacionCambioIva = (ivaActual, seleccion) =>
  Boolean(
    ivaActual?.Configurado && !ivaActual?.IvaPendiente &&
    Number(ivaActual.PorcentajeIVA) !== Number(seleccion),
  );

export const normalizarRespuestaIva = (respuesta = {}) => ({
  MercaderiaId: Number(respuesta.MercaderiaId) || 0,
  MercaderiaCodigo: String(respuesta.MercaderiaCodigo || ""),
  MercaderiaDescripcion: String(respuesta.MercaderiaDescripcion || ""),
  PorcentajeIVA: Number(respuesta.PorcentajeIVA) || 0,
  Configurado: respuesta.Configurado === true,
  IvaPendiente: respuesta.IvaPendiente === true,
  PrecioFinalCapturado: Number(respuesta.PrecioFinalCapturado) || 0,
  Error: String(respuesta.Error || ""),
});

export const normalizarResultadoConfiguracionIva = (respuesta = {}) => ({
  MercaderiaId: Number(respuesta.MercaderiaId) || 0,
  PorcentajeIVA: Number(respuesta.PorcentajeIVA) || 0,
  PrecioFinal: Number(respuesta.PrecioFinal) || 0,
  PrecioNeto: Number(respuesta.PrecioNeto) || 0,
  VentasLiberadas: Array.isArray(respuesta.VentasLiberadas) ? respuesta.VentasLiberadas.map(Number) : [],
  FacturasGeneradas: Array.isArray(respuesta.FacturasGeneradas) ? respuesta.FacturasGeneradas.map(Number) : [],
  FacturasAutorizadas: Array.isArray(respuesta.FacturasAutorizadas) ? respuesta.FacturasAutorizadas.map(Number) : [],
  Advertencias: Array.isArray(respuesta.Advertencias) ? respuesta.Advertencias.map(String) : [],
  Error: String(respuesta.Error || ""),
});

export async function consultarIvaMercaderia(EmpresaId, MercaderiaId, opciones = {}) {
  const resultado = await _Componentes_WebApiCalls_Post(
    "Mercaderias/IvaGet",
    { EmpresaId, MercaderiaId: Number(MercaderiaId) },
    { signal: opciones.signal, preservarDatosConError: true },
  );
  return resultado.error
    ? { ...resultado, data: resultado.data ? normalizarRespuestaIva(resultado.data) : undefined }
    : { data: normalizarRespuestaIva(resultado.data) };
}

export async function guardarIvaMercaderia(request) {
  if (!esAlicuotaIvaAdmitida(request?.PorcentajeIVA)) {
    return { error: "Seleccione una alícuota de IVA admitida." };
  }
  const resultado = await _Componentes_WebApiCalls_Post(
    "Mercaderias/IvaGuardar",
    {
      EmpresaId: request.EmpresaId,
      MercaderiaId: Number(request.MercaderiaId),
      PorcentajeIVA: Number(request.PorcentajeIVA),
      OperadorId: Number(request.OperadorId),
      PcId: Number(request.PcId),
    },
    { preservarDatosConError: true },
  );
  return resultado.error
    ? { ...resultado, data: resultado.data ? normalizarRespuestaIva(resultado.data) : undefined }
    : { data: normalizarRespuestaIva(resultado.data) };
}

export async function configurarIvaPendienteMercaderia(request) {
  if (!esAlicuotaIvaAdmitida(request?.PorcentajeIVA)) {
    return { error: "Seleccione una alícuota de IVA admitida." };
  }
  const resultado = await _Componentes_WebApiCalls_Post(
    "Api00557/ConfigurarIvaPendiente",
    {
      EmpresaId: request.EmpresaId,
      MercaderiaId: Number(request.MercaderiaId),
      PorcentajeIVA: Number(request.PorcentajeIVA),
      OperadorId: Number(request.OperadorId),
      PcId: Number(request.PcId),
    },
    { preservarDatosConError: true },
  );
  return resultado.error
    ? { ...resultado, data: resultado.data ? normalizarResultadoConfiguracionIva(resultado.data) : undefined }
    : { data: normalizarResultadoConfiguracionIva(resultado.data) };
}

const leerBooleano = (origen, claves) => {
  for (const clave of claves) {
    if (typeof origen?.[clave] === "boolean") return origen[clave];
  }
  return undefined;
};

export function puedeEditarIvaMercaderias({ operador, permisos } = {}) {
  const capacidad = leerBooleano(operador, [
    "PuedeEditarMercaderias",
    "puedeEditarMercaderias",
    "PuedeEditarConfiguracionFiscal",
    "puedeEditarConfiguracionFiscal",
  ]);
  if (capacidad !== undefined) return capacidad;

  if (Array.isArray(permisos)) {
    return permisos.some((permiso) => {
      const codigo = String(
        typeof permiso === "string"
          ? permiso
          : permiso?.Permiso_sCodigo || permiso?.Codigo || permiso?.sCodigo || permiso?.Descripcion || "",
      );
      const habilitado = typeof permiso === "string" ||
        permiso?.Activo === true || permiso?.Habilitado === true || permiso?.Permitido === true;
      return (codigo === "*" || /mercader|producto|fiscal|iva/i.test(codigo)) && habilitado;
    });
  }

  // Compatibilidad con instalaciones anteriores, cuyo login no informaba permisos.
  return true;
}

export const mensajeVentaConIvaFaltante = (mensaje) => {
  const texto = String(mensaje || "");
  if (!/iva|al[ií]cuota/i.test(texto) || !/configur|clasific|falt|requer/i.test(texto)) {
    return texto;
  }
  return `${texto} El producto necesita una categoría fiscal. Solicitá su clasificación y volvé a intentar; el carrito se conservará.`;
};
